import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Mock user data
interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatar: string | null;
  role: string;
  accountType: string;
  joinDate: string;
  settings: {
    notifications: boolean;
    darkMode: boolean;
    language: string;
  };
  stats: {
    totalChats: number;
    totalMessages: number;
    aiSaves: number;
    knowledgeBaseDocuments: number;
  };
}

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<UserProfile | null>(null);

  // Simulate loading user data
  useEffect(() => {
    const timer = setTimeout(() => {
      setUser(mockUserProfile);
      setEditedUser(mockUserProfile);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof UserProfile) => {
    if (!editedUser) return;

    setEditedUser({
      ...editedUser,
      [field]: e.target.value
    });
  };

  const handleSaveChanges = () => {
    if (!editedUser) return;
    setUser(editedUser);
    setIsEditing(false);
    // Here you would normally save to backend
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-300">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mb-4 text-gray-500">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
        </svg>
        <h2 className="text-xl font-bold mb-2">User Not Found</h2>
        <p className="text-gray-400 mb-4">Unable to load user profile data</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 highlight-button rounded-lg text-white font-medium shadow-lg shadow-indigo-500/20"
        >
          Return Home
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-1/2 -left-1/4 w-3/4 h-3/4 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-3/4 h-3/4 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/3 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header with Back Button */}
      <header className="bg-primary/80 backdrop-blur-md px-6 py-4 border-b border-border-color sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold text-white">User Profile</h1>
          <button
            onClick={() => navigate('/chat')}
            className="text-sm px-4 py-2 rounded-md bg-accent hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
          >
            Back to Chat
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 relative z-1">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="bg-sidebar-bg border border-border-color rounded-xl shadow-lg p-6 flex flex-col items-center">
            <div className="h-32 w-32 rounded-full bg-gray-700 flex items-center justify-center mb-4 border-4 border-accent overflow-hidden">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-4xl font-semibold text-gray-300">
                  {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </span>
              )}
            </div>

            <h2 className="text-2xl font-bold text-white mb-1">{user.name}</h2>
            <p className="text-gray-400 mb-4">{user.email}</p>

            <div className="w-full pt-4 border-t border-border-color mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">Role</span>
                <span className="text-white font-medium">{user.role}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-400">Account Type</span>
                <span className="bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded-full text-xs font-medium">
                  {user.accountType}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Member Since</span>
                <span className="text-white font-medium">{new Date(user.joinDate).toLocaleDateString()}</span>
              </div>
            </div>

            <button
              onClick={handleEditToggle}
              className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
                isEditing
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "highlight-button text-white shadow-lg shadow-indigo-500/20"
              }`}
            >
              {isEditing ? "Cancel Editing" : "Edit Profile"}
            </button>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Information Section */}
            <div className="bg-sidebar-bg border border-border-color rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-white">User Information</h3>

              <AnimatePresence mode="wait">
                {isEditing ? (
                  <motion.div
                    key="edit-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">Name</label>
                      <input
                        type="text"
                        value={editedUser?.name || ''}
                        onChange={(e) => handleInputChange(e, 'name')}
                        className="w-full px-4 py-3 rounded-lg bg-tertiary border border-border-color focus:border-accent outline-none text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">Email</label>
                      <input
                        type="email"
                        value={editedUser?.email || ''}
                        onChange={(e) => handleInputChange(e, 'email')}
                        className="w-full px-4 py-3 rounded-lg bg-tertiary border border-border-color focus:border-accent outline-none text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">Role</label>
                      <input
                        type="text"
                        value={editedUser?.role || ''}
                        onChange={(e) => handleInputChange(e, 'role')}
                        className="w-full px-4 py-3 rounded-lg bg-tertiary border border-border-color focus:border-accent outline-none text-white"
                      />
                    </div>

                    <button
                      onClick={handleSaveChanges}
                      className="w-full px-4 py-3 highlight-button rounded-lg text-white font-medium shadow-lg shadow-indigo-500/20"
                    >
                      Save Changes
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="view-profile"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-gray-400 text-sm">Name</h4>
                        <p className="text-white">{user.name}</p>
                      </div>

                      <div>
                        <h4 className="text-gray-400 text-sm">Email</h4>
                        <p className="text-white">{user.email}</p>
                      </div>

                      <div>
                        <h4 className="text-gray-400 text-sm">Role</h4>
                        <p className="text-white">{user.role}</p>
                      </div>

                      <div>
                        <h4 className="text-gray-400 text-sm">Account Type</h4>
                        <p className="text-white">{user.accountType}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Stats */}
            <div className="bg-sidebar-bg border border-border-color rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-white">Usage Statistics</h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-white mb-1">{user.stats.totalChats}</div>
                  <div className="text-gray-400 text-sm">Total Chats</div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-white mb-1">{user.stats.totalMessages}</div>
                  <div className="text-gray-400 text-sm">Messages</div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-white mb-1">{user.stats.aiSaves}</div>
                  <div className="text-gray-400 text-sm">AI Saves</div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-white mb-1">{user.stats.knowledgeBaseDocuments}</div>
                  <div className="text-gray-400 text-sm">Documents</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mock data
const mockUserProfile: UserProfile = {
  id: 'user123',
  name: 'John Doe',
  email: 'john.doe@example.com',
  avatar: null,
  role: 'Software Developer',
  accountType: 'Free',
  joinDate: '2023-05-15T00:00:00Z',
  settings: {
    notifications: true,
    darkMode: true,
    language: 'en'
  },
  stats: {
    totalChats: 42,
    totalMessages: 854,
    aiSaves: 17,
    knowledgeBaseDocuments: 8
  }
};

export default Profile;
